/**
 * TasksPanel — to-dos attached to a customer, prospect, or account.
 *
 * Self-contained like DocumentsPanel: subscribes to the `tasks`
 * collection itself and filters to the entity it's handed (by personId
 * or accountId), so it can drop into the person detail pane or the
 * account detail modal. Adds a quick inline composer (title + due date +
 * priority) that ties the new task to the entity, then lists the task
 * rows with a complete checkbox, status select, and delete.
 */

import { useEffect, useMemo, useState } from 'react';
import { Calendar, Check, Flag, Plus, Trash2 } from 'lucide-react';
import { C } from '../lib/tokens';
import {
  createTask,
  deleteTask,
  setTaskStatus,
  subscribeTasks,
} from '../lib/db/tasks';
import type { ProjectTask, TaskPriority, TaskStatus } from '../lib/db/types';
import { useAuth } from '../lib/auth/AuthProvider';

interface TasksPanelProps {
  entityType: 'person' | 'account';
  entityId: string;
  entityName: string;
}

const STATUSES: TaskStatus[] = ['todo', 'in-progress', 'blocked', 'done'];
const PRIORITIES: TaskPriority[] = ['low', 'medium', 'high', 'urgent'];

const PRIORITY_COLOR: Record<TaskPriority, string> = {
  low: C.textDim,
  medium: C.blueElectric,
  high: C.orange,
  urgent: C.orangeWarm,
};

export function TasksPanel({ entityType, entityId, entityName }: TasksPanelProps) {
  const { user } = useAuth();
  const canMutate = user?.role !== 'viewer';
  const [all, setAll] = useState<ProjectTask[]>([]);
  const [title, setTitle] = useState('');
  const [due, setDue] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('medium');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const unsub = subscribeTasks(setAll);
    return unsub;
  }, []);

  const tasks = useMemo(() => {
    const key = entityType === 'person' ? 'personId' : 'accountId';
    return all
      .filter((t) => t[key] === entityId)
      // Open tasks first (by due date), completed sink to the bottom.
      .sort((a, b) => {
        const aDone = a.status === 'done' ? 1 : 0;
        const bDone = b.status === 'done' ? 1 : 0;
        if (aDone !== bDone) return aDone - bDone;
        const ad = a.dueDate ?? '9999';
        const bd = b.dueDate ?? '9999';
        if (ad !== bd) return ad < bd ? -1 : 1;
        return a.createdAt < b.createdAt ? 1 : -1;
      });
  }, [all, entityId, entityType]);

  const openCount = tasks.filter((t) => t.status !== 'done').length;

  const add = async () => {
    const t = title.trim();
    if (!t) return;
    setBusy(true);
    try {
      await createTask({
        title: t,
        priority,
        dueDate: due || null,
        ...(entityType === 'person'
          ? { personId: entityId, personName: entityName }
          : { accountId: entityId, accountName: entityName }),
        createdBy: 'You',
      });
      setTitle('');
      setDue('');
      setPriority('medium');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <p className="t-body" style={{ color: C.textMuted, margin: 0 }}>
        To-dos for {entityName}.
        {openCount > 0 ? ` ${openCount} open.` : ' All clear.'}
      </p>

      {/* Composer */}
      {canMutate && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 150px 120px auto',
            gap: 8,
            alignItems: 'center',
          }}
        >
          <input
            className="glass-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') add();
            }}
            placeholder="Add a task…"
          />
          <input
            className="glass-input"
            type="date"
            value={due}
            onChange={(e) => setDue(e.target.value)}
            title="Due date"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          />
          <select
            className="glass-input"
            value={priority}
            onChange={(e) => setPriority(e.target.value as TaskPriority)}
            title="Priority"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <button
            className="glass-btn glass-btn--primary"
            onClick={add}
            disabled={busy || !title.trim()}
            title="Add task"
          >
            <Plus size={13} /> Add
          </button>
        </div>
      )}

      {tasks.length === 0 && (
        <div
          className="t-meta"
          style={{
            padding: '18px 12px',
            textAlign: 'center',
            border: '1px dashed rgba(255,255,255,0.08)',
            borderRadius: 12,
          }}
        >
          {canMutate ? 'No tasks yet. Add one above.' : 'No tasks yet.'}
        </div>
      )}

      {tasks.map((task) => (
        <TaskRow key={task.id} task={task} canMutate={canMutate} />
      ))}
    </div>
  );
}

function TaskRow({ task, canMutate }: { task: ProjectTask; canMutate: boolean }) {
  const done = task.status === 'done';
  const overdue =
    !done && task.dueDate != null && task.dueDate < new Date().toISOString().slice(0, 10);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        borderRadius: 10,
        border: '1px solid rgba(255,255,255,0.08)',
        background: 'rgba(255,255,255,0.04)',
        opacity: done ? 0.6 : 1,
      }}
    >
      <button
        type="button"
        onClick={canMutate ? () => setTaskStatus(task.id, done ? 'todo' : 'done') : undefined}
        disabled={!canMutate}
        title={canMutate ? (done ? 'Mark as to-do' : 'Mark done') : done ? 'Done' : 'To-do'}
        style={{
          width: 20,
          height: 20,
          flexShrink: 0,
          borderRadius: 6,
          display: 'inline-grid',
          placeItems: 'center',
          cursor: canMutate ? 'pointer' : 'default',
          border: `1px solid ${done ? C.green : 'rgba(255,255,255,0.25)'}`,
          background: done ? C.green : 'transparent',
          color: done ? '#0a0a10' : 'transparent',
        }}
      >
        <Check size={13} strokeWidth={3} />
      </button>

      <span style={{ flex: 1, minWidth: 0 }}>
        <span
          className="t-card-title"
          style={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textDecoration: done ? 'line-through' : 'none',
          }}
        >
          {task.title}
        </span>
        <span
          className="t-meta"
          style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}
        >
          {task.dueDate && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                color: overdue ? C.orangeWarm : C.textDim,
                fontWeight: overdue ? 700 : 400,
              }}
            >
              <Calendar size={10} /> {task.dueDate}
              {overdue ? ' · overdue' : ''}
            </span>
          )}
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              color: PRIORITY_COLOR[task.priority],
            }}
          >
            <Flag size={10} /> {task.priority}
          </span>
          {task.assignee && <span style={{ color: C.textDim }}>@{task.assignee}</span>}
        </span>
      </span>

      <select
        className="glass-input"
        value={task.status}
        onChange={(e) => setTaskStatus(task.id, e.target.value as TaskStatus)}
        title="Status"
        disabled={!canMutate}
        style={{ fontFamily: 'Manrope, sans-serif', maxWidth: 130 }}
      >
        {STATUSES.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>

      {canMutate && (
        <button
          className="glass-btn"
          onClick={() => {
            if (window.confirm(`Delete task “${task.title}”?`)) deleteTask(task.id);
          }}
          title="Delete task"
        >
          <Trash2 size={13} />
        </button>
      )}
    </div>
  );
}
